import { useCallback, useState } from 'react';

import { useCheckPaymentsDebtMutation } from 'core/graphql';
import {
  partitionBulkResults,
  IBulkOutcome,
  IBulkPartition,
} from 'core/utils/bulkResult';

/**
 * Minimal shape the bulk debtor reminder needs from each row it acts on.
 *
 * The backend reminder action is `checkStudentDebt(agreementsInput: [String])`
 * (the `CheckPaymentsDebtMutation` in `mutations/debtors.ts`), whose argument
 * is a list of agreement **titles** (verified against `schema.json`:
 * `agreementsInput: [String]`). Per the design fallback this doubles as the
 * per-debtor reminder action since no dedicated reminder mutation exists.
 *
 * The hook is generic over the caller's row type so the failed/succeeded
 * partition carries the full original item back to the caller (task 15.4) for
 * reporting; it only requires an `agreementTitle` to drive the mutation.
 */
export interface IRemindDebtorItem {
  agreementTitle: string;
}

interface IUseRemindDebtors<TItem extends IRemindDebtorItem> {
  /**
   * Sends a debt reminder for each item INDEPENDENTLY and returns a
   * `partitionBulkResults` partition (Req 10.6, Property 11).
   *
   * PER-ITEM (not batched) BY DESIGN: although `checkStudentDebt` accepts the
   * whole title list in one call, the hook awaits one mutation per item so a
   * single failing reminder is isolated to its own row instead of failing the
   * whole batch. This yields honest partial-failure semantics — successes are
   * preserved and the caller learns exactly which items failed.
   *
   * The hook does NOT refetch anything; the caller decides what to do with the
   * `succeeded`/`failed` partition (e.g. a localized summary toast).
   */
  remindDebtors: (
    items: ReadonlyArray<TItem>,
  ) => Promise<IBulkPartition<TItem>>;
  /** `true` while a bulk reminder run is in flight. */
  pending: boolean;
}

/**
 * Bulk "remind all debtors" hook (Req 10.4–10.6). The caller passes the
 * already-`selectDebtors`-filtered visible rows; this hook reminds each one
 * independently and partitions the outcome into succeeded/failed without loss.
 */
export const useRemindDebtors = <
  TItem extends IRemindDebtorItem
>(): IUseRemindDebtors<TItem> => {
  const [run] = useCheckPaymentsDebtMutation();
  const [pending, setPending] = useState(false);

  const remindDebtors = useCallback(
    async (items: ReadonlyArray<TItem>): Promise<IBulkPartition<TItem>> => {
      setPending(true);
      try {
        const outcomes: Array<IBulkOutcome> = await Promise.all(
          items.map(
            async (item): Promise<IBulkOutcome> => {
              try {
                const result = await run({
                  variables: { agreements: [item.agreementTitle] },
                });
                const ok = Boolean(result.data?.checkStudentDebt?.success);
                if (ok) return { ok: true };
                return {
                  ok: false,
                  error:
                    result.data?.checkStudentDebt?.customErrors?.[0] ??
                    undefined,
                };
              } catch (error) {
                return { ok: false, error };
              }
            },
          ),
        );
        return partitionBulkResults(items, outcomes);
      } finally {
        setPending(false);
      }
    },
    [run],
  );

  return { remindDebtors, pending };
};
